import ArrowDropDown from "@mui/icons-material/ArrowDropDown";
import UploadFile from "@mui/icons-material/UploadFile";
import { Button, ButtonProps, Menu, MenuItem } from "@mui/material";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { ShippingDocumentKind } from "generated-graphql/graphql";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useQueueManifestDocumentExtraction } from "hooks/useQueueManifestDocumentExtraction";
import { useState } from "react";

import { ShippingRecordUploadFlow, UploadableShippingKind } from "./ShippingRecordUploadFlow";

/**
 * The Shipment Documents list's add control, and the one owner of the upload
 * flow it starts.
 *
 * Manifest upload needs `canQueueManifestExtraction`; bill of lading upload
 * needs the resolved `ManifestViewsV2` flag. With both available, one "Add
 * Shipping Record" button opens a menu to choose. With one, a single button
 * named for what it does. With neither, nothing renders.
 *
 * The flow stays mounted once a kind has been chosen, with only `open` toggled,
 * so a bill's review step survives its upload dialog closing.
 *
 * `onSaved` reaches the manifest kind only. A bill of lading's apply mutation
 * refetches the list's query itself (`useApplyReviewedBillOfLading`), so
 * routing that side through the host too would issue the same query twice.
 */
export function AddShippingRecordButton({
  tenantId,
  facilityId,
  size,
  disabled,
  onSaved,
}: {
  tenantId: string;
  facilityId?: string;
  size?: ButtonProps["size"];
  disabled?: boolean;
  /** Called once an added manifest's review is applied, so a host can refetch. */
  onSaved?: () => void;
}) {
  const { canQueueManifestExtraction } = useQueueManifestDocumentExtraction();
  const { featureFlags } = useFeatureFlags();
  const canAddBillOfLading = !!featureFlags[FeatureFlagKey.ManifestViewsV2];

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [kind, setKind] = useState<UploadableShippingKind | null>(null);
  const [open, setOpen] = useState(false);

  if (!canQueueManifestExtraction && !canAddBillOfLading) {
    return null;
  }

  const choose = (chosen: UploadableShippingKind) => {
    setAnchorEl(null);
    setKind(chosen);
    setOpen(true);
  };

  const control =
    canQueueManifestExtraction && canAddBillOfLading ? (
      <>
        <Button
          variant="contained"
          size={size}
          disabled={disabled}
          endIcon={<ArrowDropDown />}
          onClick={(event) => setAnchorEl(event.currentTarget)}
        >
          Add Shipping Record
        </Button>
        <Menu anchorEl={anchorEl} open={anchorEl !== null} onClose={() => setAnchorEl(null)}>
          <MenuItem onClick={() => choose(ShippingDocumentKind.BillOfLading)}>
            Bill of Lading
          </MenuItem>
          <MenuItem onClick={() => choose(ShippingDocumentKind.Manifest)}>Manifest</MenuItem>
        </Menu>
      </>
    ) : (
      <Button
        variant="contained"
        size={size}
        disabled={disabled}
        startIcon={<UploadFile />}
        onClick={() =>
          choose(
            canQueueManifestExtraction
              ? ShippingDocumentKind.Manifest
              : ShippingDocumentKind.BillOfLading
          )
        }
      >
        {canQueueManifestExtraction ? "Add Manifest" : "Add Bill of Lading"}
      </Button>
    );

  return (
    <>
      {control}
      {kind && (
        <ShippingRecordUploadFlow
          documentKind={kind}
          tenantId={tenantId}
          facilityId={facilityId}
          open={open}
          onClose={() => setOpen(false)}
          onSaved={kind === ShippingDocumentKind.Manifest ? onSaved : undefined}
        />
      )}
    </>
  );
}
